import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';
import { authService } from '../../features/authentication/services/authService';
import { tokenStorage } from '../../services/auth/tokenStorage';
import SessionTimeoutModal from '../modals/SessionTimeoutModal';
import { SESSION_INACTIVITY_TIMEOUT, SESSION_WARNING_TIME } from '../../constants/session';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

export default function SessionTimeoutManager() {
  const { isAuthenticated, logout } = useAuth();
  const toast = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState(SESSION_WARNING_TIME);

  const lastActivityRef = useRef(Date.now());
  const inactivityTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const isOpenRef = useRef(isOpen);
  const logoutRef = useRef(logout);
  const toastRef = useRef(toast);
  const resetInactivityRef = useRef(() => {});

  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  useEffect(() => { logoutRef.current = logout; }, [logout]);
  useEffect(() => { toastRef.current = toast; }, [toast]);

  const clearTimers = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  }, []);

  const handleLogout = useCallback(() => {
    clearTimers();
    setIsOpen(false);
    // The central auth hook clears local session immediately and runs the backend
    // logout request in the background, so the modal can close right away.
    Promise.resolve(logoutRef.current()).catch(() => {});
  }, [clearTimers]);

  const handleContinue = useCallback(async () => {
    clearTimers();
    setIsOpen(false);
    setRemainingMs(SESSION_WARNING_TIME);
    lastActivityRef.current = Date.now();
    resetInactivityRef.current();

    const refreshToken = tokenStorage.getRefreshToken();
    if (!refreshToken) {
      handleLogout();
      return;
    }

    try {
      const data = await authService.refresh(refreshToken);
      if (data?.accessToken) tokenStorage.setAccessToken(data.accessToken);
      if (data?.refreshToken) tokenStorage.setRefreshToken(data.refreshToken);
    } catch (err) {
      // Refresh failed; clear the session and redirect
      toastRef.current.error('Your session has expired. Please log in again.');
      handleLogout();
    }
  }, [clearTimers, handleLogout]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      setIsOpen(false);
      return;
    }

    const startInactivityTimer = () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(() => {
        setRemainingMs(SESSION_WARNING_TIME);
        setIsOpen(true);
      }, SESSION_INACTIVITY_TIMEOUT - SESSION_WARNING_TIME);
    };

    resetInactivityRef.current = startInactivityTimer;
    startInactivityTimer();

    const handleActivity = () => {
      if (isOpenRef.current) return;
      lastActivityRef.current = Date.now();
      startInactivityTimer();
    };

    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, handleActivity, { passive: true }));

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, handleActivity));
    };
  }, [isAuthenticated, clearTimers]);

  useEffect(() => {
    if (!isOpen) {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      return;
    }

    countdownIntervalRef.current = setInterval(() => {
      setRemainingMs((prev) => {
        const next = prev - 1000;
        if (next <= 0) {
          clearInterval(countdownIntervalRef.current);
          handleLogout();
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [isOpen, handleLogout]);

  if (!isAuthenticated) return null;

  return (
    <SessionTimeoutModal
      isOpen={isOpen}
      remainingMs={remainingMs}
      onContinue={handleContinue}
      onLogout={handleLogout}
    />
  );
}
